import React, { useState, useEffect, useCallback } from 'react';
import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    deleteDoc,
    doc,
    updateDoc,
    orderBy,
    serverTimestamp,
    Timestamp,
    arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Users,
    Mail,
    Copy,
    Trash2,
    UserPlus,
    Clock,
    CheckCircle,
    AlertCircle,
    UserMinus,
    RefreshCw,
    Factory
} from 'lucide-react';

function SalesRepManagement({ user }) {
    const [activeTab, setActiveTab] = useState('reps'); // reps, invites
    const [salesRepList, setSalesRepList] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);

    const companyId = user?.companyId;
    const companyName = user?.companyName || user?.businessName || '';

    const loadData = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);

        try {
            // 영업사원 목록 조회
            const repQuery = query(
                collection(db, 'users'),
                where('companyId', '==', companyId),
                where('userType', '==', 'sales_rep')
            );
            const repSnapshot = await getDocs(repQuery);
            const repData = repSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setSalesRepList(repData);

            // 초대 목록 조회
            const inviteQuery = query(
                collection(db, 'salesRepInvitations'),
                where('companyId', '==', companyId),
                orderBy('createdAt', 'desc')
            );
            const inviteSnapshot = await getDocs(inviteQuery);
            const inviteData = inviteSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setInvitations(inviteData);
        } catch (error) {
            console.error('데이터 로드 실패:', error);
            setMessage({ type: 'error', text: '데이터를 불러오는데 실패했습니다.' });
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // 초대 코드 생성
    const generateInviteCode = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    };

    // 초대 생성
    const createInvitation = async () => {
        try {
            const code = generateInviteCode();
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + 7); // 7일 후 만료

            await addDoc(collection(db, 'salesRepInvitations'), {
                companyId: companyId,
                companyName: companyName,
                inviteCode: code,
                status: 'pending',
                createdAt: serverTimestamp(),
                expiresAt: Timestamp.fromDate(expiresAt),
                createdBy: user.uid
            });

            setMessage({ type: 'success', text: '초대 코드가 생성되었습니다.' });
            loadData();
        } catch (error) {
            console.error('초대 생성 실패:', error);
            setMessage({ type: 'error', text: '초대 생성에 실패했습니다.' });
        }
    };

    // 초대 삭제
    const deleteInvitation = async (invitationId) => {
        if (!window.confirm('이 초대를 삭제하시겠습니까?')) return;

        try {
            await deleteDoc(doc(db, 'salesRepInvitations', invitationId));
            setMessage({ type: 'success', text: '초대가 삭제되었습니다.' });
            loadData();
        } catch (error) {
            console.error('초대 삭제 실패:', error);
            setMessage({ type: 'error', text: '초대 삭제에 실패했습니다.' });
        }
    };

    // 영업사원 해제
    const removeSalesRep = async (repId, repName) => {
        if (!window.confirm(`${repName}님을 영업사원에서 해제하시겠습니까?\n해제 후에는 회사 상품을 관리할 수 없습니다.`)) return;

        try {
            // 사용자 정보에서 회사 정보 제거
            await updateDoc(doc(db, 'users', repId), {
                companyId: null,
                companyName: null,
                userType: 'individual',
                role: null
            });

            // materialCompanies의 salesRepIds에서 제거
            if (companyId) {
                await updateDoc(doc(db, 'materialCompanies', companyId), {
                    salesRepIds: arrayRemove(repId)
                });
            }

            setMessage({ type: 'success', text: `${repName}님이 영업사원에서 해제되었습니다.` });
            loadData();
        } catch (error) {
            console.error('영업사원 해제 실패:', error);
            setMessage({ type: 'error', text: '영업사원 해제에 실패했습니다.' });
        }
    };

    // 클립보드 복사
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setMessage({ type: 'success', text: '클립보드에 복사되었습니다.' });
    };

    // 날짜 포맷
    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('ko-KR');
    };

    // 만료 확인
    const isExpired = (expiresAt) => {
        if (!expiresAt) return false;
        const expDate = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
        return expDate < new Date();
    };

    const pendingInvitations = invitations.filter(inv => inv.status === 'pending');
    const usedInvitations = invitations.filter(inv => inv.status === 'used');

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>로딩 중...</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>
                    <Factory size={28} color="#059669" />
                    영업사원 관리
                </h1>
                <p style={styles.subtitle}>{companyName}의 영업사원을 관리합니다</p>
            </div>

            {message && (
                <div style={{
                    ...styles.message,
                    backgroundColor: message.type === 'success' ? '#d1fae5' : '#fee2e2',
                    color: message.type === 'success' ? '#065f46' : '#991b1b'
                }}>
                    {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                    {message.text}
                    <button onClick={() => setMessage(null)} style={styles.messageClose}>×</button>
                </div>
            )}

            <div style={styles.stats}>
                <div style={{ ...styles.statCard, borderColor: '#059669' }}>
                    <div style={{ ...styles.statValue, color: '#059669' }}>{salesRepList.length}</div>
                    <div style={styles.statLabel}>현재 영업사원</div>
                </div>
                <div style={{ ...styles.statCard, borderColor: '#f59e0b' }}>
                    <div style={{ ...styles.statValue, color: '#f59e0b' }}>{pendingInvitations.length}</div>
                    <div style={styles.statLabel}>대기중 초대</div>
                </div>
            </div>

            <div style={styles.tabs}>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'reps' ? styles.activeTabGreen : {}) }}
                    onClick={() => setActiveTab('reps')}
                >
                    <Users size={18} />
                    영업사원 목록 ({salesRepList.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'invites' ? styles.activeTabGreen : {}) }}
                    onClick={() => setActiveTab('invites')}
                >
                    <Mail size={18} />
                    초대 관리 ({pendingInvitations.length})
                </button>
                <button onClick={loadData} style={styles.refreshBtn}>
                    <RefreshCw size={18} />
                </button>
            </div>

            {activeTab === 'reps' && (
                <div style={styles.content}>
                    {salesRepList.length === 0 ? (
                        <div style={styles.empty}>
                            <Users size={48} color="#9ca3af" />
                            <p>등록된 영업사원이 없습니다</p>
                            <p style={styles.emptyHint}>초대 코드를 생성하여 영업사원을 초대하세요</p>
                        </div>
                    ) : (
                        <div style={styles.list}>
                            {salesRepList.map(rep => (
                                <div key={rep.id} style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div style={styles.avatarGreen}>
                                            {(rep.name || rep.email || '?')[0].toUpperCase()}
                                        </div>
                                        <div style={styles.cardInfo}>
                                            <h3 style={styles.cardName}>{rep.name || '이름 없음'}</h3>
                                            <p style={styles.cardEmail}>{rep.email}</p>
                                            {rep.phone && <p style={styles.cardPhone}>{rep.phone}</p>}
                                            <p style={styles.cardDate}>가입일: {formatDate(rep.createdAt)}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => removeSalesRep(rep.id, rep.name || rep.email)}
                                        style={styles.removeBtn}
                                    >
                                        <UserMinus size={16} />
                                        해제
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'invites' && (
                <div style={styles.content}>
                    <button onClick={createInvitation} style={styles.createBtnGreen}>
                        <UserPlus size={20} />
                        새 초대 코드 생성
                    </button>

                    {pendingInvitations.length > 0 && (
                        <>
                            <h3 style={styles.sectionTitle}>대기중인 초대</h3>
                            <div style={styles.list}>
                                {pendingInvitations.map(inv => {
                                    const expired = isExpired(inv.expiresAt);
                                    return (
                                        <div key={inv.id} style={{
                                            ...styles.inviteCard,
                                            opacity: expired ? 0.6 : 1
                                        }}>
                                            <div style={styles.inviteHeader}>
                                                <div style={{
                                                    ...styles.inviteStatus,
                                                    backgroundColor: expired ? '#fee2e2' : '#fef3c7',
                                                    color: expired ? '#991b1b' : '#92400e'
                                                }}>
                                                    {expired ? <AlertCircle size={16} /> : <Clock size={16} />}
                                                    {expired ? '만료됨' : '대기중'}
                                                </div>
                                                <span style={styles.inviteDate}>
                                                    생성: {formatDate(inv.createdAt)}
                                                </span>
                                            </div>
                                            {!expired && (
                                                <>
                                                    <div style={styles.codeBox}>
                                                        <span style={styles.code}>{inv.inviteCode}</span>
                                                        <button
                                                            onClick={() => copyToClipboard(inv.inviteCode)}
                                                            style={styles.copyBtn}
                                                        >
                                                            <Copy size={16} />
                                                        </button>
                                                    </div>
                                                    <p style={styles.expireDate}>
                                                        만료: {formatDate(inv.expiresAt)}
                                                    </p>
                                                </>
                                            )}
                                            <button
                                                onClick={() => deleteInvitation(inv.id)}
                                                style={styles.deleteBtn}
                                            >
                                                <Trash2 size={14} />
                                                삭제
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {usedInvitations.length > 0 && (
                        <>
                            <h3 style={styles.sectionTitle}>사용된 초대</h3>
                            <div style={styles.list}>
                                {usedInvitations.map(inv => (
                                    <div key={inv.id} style={styles.usedInviteCard}>
                                        <div style={styles.inviteHeader}>
                                            <div style={styles.usedStatus}>
                                                <CheckCircle size={16} />
                                                사용됨
                                            </div>
                                            <span style={styles.inviteDate}>
                                                {formatDate(inv.usedAt || inv.createdAt)}
                                            </span>
                                        </div>
                                        <p style={styles.usedCode}>{inv.inviteCode}</p>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {invitations.length === 0 && (
                        <div style={styles.empty}>
                            <Mail size={48} color="#9ca3af" />
                            <p>초대 내역이 없습니다</p>
                            <p style={styles.emptyHint}>위 버튼을 클릭하여 초대 코드를 생성하세요</p>
                        </div>
                    )}

                    <div style={styles.helpBox}>
                        <h4>초대 코드 사용 방법</h4>
                        <ol>
                            <li>초대 코드를 생성합니다</li>
                            <li>코드를 복사하여 영업사원에게 전달합니다</li>
                            <li>영업사원이 회원가입 시 "개인 회원" → "영업사원으로 가입하기" 선택</li>
                            <li>초대 코드를 입력하면 자동으로 영업사원으로 등록됩니다</li>
                        </ol>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '24px',
    },
    header: {
        marginBottom: '24px',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '24px',
        fontWeight: '700',
        color: '#1f2937',
        margin: 0,
    },
    subtitle: {
        color: '#6b7280',
        marginTop: '8px',
    },
    loading: {
        textAlign: 'center',
        padding: '48px',
        color: '#6b7280',
    },
    message: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        borderRadius: '8px',
        marginBottom: '16px',
    },
    messageClose: {
        marginLeft: 'auto',
        background: 'none',
        border: 'none',
        fontSize: '20px',
        cursor: 'pointer',
        opacity: 0.7,
    },
    stats: {
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
    },
    statCard: {
        flex: 1,
        backgroundColor: '#f8fafc',
        padding: '20px',
        borderRadius: '12px',
        textAlign: 'center',
        borderBottom: '3px solid',
    },
    statValue: {
        fontSize: '32px',
        fontWeight: '700',
    },
    statLabel: {
        fontSize: '14px',
        color: '#6b7280',
        marginTop: '4px',
    },
    tabs: {
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '8px',
    },
    tab: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        border: 'none',
        background: 'none',
        fontSize: '14px',
        fontWeight: '500',
        color: '#6b7280',
        cursor: 'pointer',
        borderRadius: '8px',
    },
    activeTabGreen: {
        backgroundColor: '#ecfdf5',
        color: '#059669',
    },
    refreshBtn: {
        marginLeft: 'auto',
        padding: '10px',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        color: '#6b7280',
        borderRadius: '8px',
    },
    content: {
        minHeight: '300px',
    },
    empty: {
        textAlign: 'center',
        padding: '48px',
        color: '#6b7280',
    },
    emptyHint: {
        fontSize: '14px',
        color: '#9ca3af',
        marginTop: '8px',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    card: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    avatarGreen: {
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        backgroundColor: '#d1fae5',
        color: '#059669',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        fontWeight: '700',
    },
    cardInfo: {
        flex: 1,
    },
    cardName: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
    },
    cardEmail: {
        margin: '4px 0 0',
        fontSize: '13px',
        color: '#6b7280',
    },
    cardPhone: {
        margin: '2px 0 0',
        fontSize: '12px',
        color: '#9ca3af',
    },
    cardDate: {
        margin: '4px 0 0',
        fontSize: '12px',
        color: '#9ca3af',
    },
    removeBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    createBtnGreen: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: '100%',
        padding: '14px',
        backgroundColor: '#059669',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        marginBottom: '24px',
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '12px',
        marginTop: '24px',
    },
    inviteCard: {
        padding: '16px',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
    },
    inviteHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
    },
    inviteStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '500',
    },
    inviteDate: {
        fontSize: '12px',
        color: '#9ca3af',
    },
    codeBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        marginBottom: '8px',
    },
    code: {
        fontSize: '20px',
        fontWeight: '700',
        fontFamily: 'monospace',
        letterSpacing: '2px',
        color: '#1f2937',
    },
    copyBtn: {
        padding: '8px',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        cursor: 'pointer',
        color: '#6b7280',
    },
    expireDate: {
        fontSize: '12px',
        color: '#6b7280',
        margin: 0,
    },
    deleteBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginTop: '12px',
        padding: '6px 10px',
        backgroundColor: 'transparent',
        color: '#dc2626',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        fontSize: '12px',
        cursor: 'pointer',
    },
    usedInviteCard: {
        padding: '12px 16px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
    },
    usedStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        backgroundColor: '#d1fae5',
        color: '#065f46',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '500',
    },
    usedCode: {
        fontSize: '14px',
        fontFamily: 'monospace',
        color: '#9ca3af',
        margin: '8px 0 0',
    },
    helpBox: {
        marginTop: '32px',
        padding: '20px',
        backgroundColor: '#ecfdf5',
        borderRadius: '12px',
        border: '1px solid #a7f3d0',
    },
};

export default SalesRepManagement;
